// src/utils/ruleLayers.ts
//
// Pure, unit-testable helpers for mutating a routing rule's ordered layer
// array (Story 1.5). Every helper returns a NEW array and — critically —
// preserves each existing layer's `layer_id` byte-identical (AD-2: Story 3.4's
// round-robin cursors key on it). Only newLayer mints a new id. Kept free of
// React/Supabase so the layer-identity discipline is testable in isolation.

import type { YakRuleLayer, YakRuleMatchAttribute } from '../types/yak'

// Mint a fresh layer with a new stable id. crypto.randomUUID is available in
// the browser and in the jsdom/node test env.
export function newLayer(
  position: string,
  matchAttributes: YakRuleMatchAttribute[]
): YakRuleLayer {
  return {
    layer_id: crypto.randomUUID(),
    position: position.trim(),
    match_attributes: [...matchAttributes],
  }
}

// Swap the layer at `index` with its neighbour. Out-of-range / at-boundary
// moves return an unchanged copy. Every layer_id is left untouched.
export function moveLayer(
  layers: YakRuleLayer[],
  index: number,
  direction: 'up' | 'down'
): YakRuleLayer[] {
  const target = direction === 'up' ? index - 1 : index + 1
  if (
    index < 0 ||
    index >= layers.length ||
    target < 0 ||
    target >= layers.length
  ) {
    return [...layers]
  }
  const next = [...layers]
  ;[next[index], next[target]] = [next[target], next[index]]
  return next
}

// Patch a single layer by id (position and/or matchers); sibling ids and the
// patched layer's own id are never touched.
export function updateLayer(
  layers: YakRuleLayer[],
  layerId: string,
  patch: Partial<Pick<YakRuleLayer, 'position' | 'match_attributes'>>
): YakRuleLayer[] {
  return layers.map(layer =>
    layer.layer_id === layerId ? { ...layer, ...patch } : layer
  )
}

// Drop a layer by id; the remaining ids stay as they were.
export function removeLayer(
  layers: YakRuleLayer[],
  layerId: string
): YakRuleLayer[] {
  return layers.filter(layer => layer.layer_id !== layerId)
}

// The layers whose position has no active team member (AC 5's warning). Match
// is trimmed exact — positionCounts is keyed by trimmed position label; a
// count of 0 or a missing key both mean "nobody holds this position".
export function unmatchedPositions(
  layers: YakRuleLayer[],
  positionCounts: Record<string, number>
): YakRuleLayer[] {
  return layers.filter(layer => !(positionCounts[layer.position.trim()] > 0))
}
